import { apiClient } from "@/lib/api";
import {
  StandardResponse,
  MessageResponse,
  ErrorResponse,
  isErrorResponse,
} from "@/types/api-response";
import type {
  TPaygateConfigDto,
  SaveTPaygateConfigRequest,
  SupportedBankDto,
  ConnectedBankDto,
  ConnectBankRequest,
  ConnectBankResult,
  ConfirmOtpRequest,
  ConfirmOtpResult,
  ConnectUrlResult,
  DisconnectBankRequest,
  SimulateWebhookRequest,
  WebhookSimulationResult,
} from "@/types/tpaygate";

export const adminTPaygateService = {
  async getConfig(): Promise<TPaygateConfigDto> {
    const response = await apiClient.get<
      StandardResponse<TPaygateConfigDto> | ErrorResponse
    >("/api/v1/admin/tpaygate/config");
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async saveConfig(
    request: SaveTPaygateConfigRequest,
  ): Promise<TPaygateConfigDto> {
    const response = await apiClient.put<
      StandardResponse<TPaygateConfigDto> | ErrorResponse
    >("/api/v1/admin/tpaygate/config", request);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async getSupportedBanks(): Promise<SupportedBankDto[]> {
    const response = await apiClient.get<
      StandardResponse<SupportedBankDto[]> | ErrorResponse
    >("/api/v1/admin/tpaygate/supported-banks");
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async getConnectedBanks(): Promise<ConnectedBankDto[]> {
    const response = await apiClient.get<
      StandardResponse<ConnectedBankDto[]> | ErrorResponse
    >("/api/v1/admin/tpaygate/banks");
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async getConnectUrl(bankCode: string): Promise<ConnectUrlResult> {
    const response = await apiClient.get<
      StandardResponse<ConnectUrlResult> | ErrorResponse
    >("/api/v1/admin/tpaygate/connect-url", { params: { bankCode } });
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async connectBank(request: ConnectBankRequest): Promise<ConnectBankResult> {
    const response = await apiClient.post<
      StandardResponse<ConnectBankResult> | ErrorResponse
    >("/api/v1/admin/tpaygate/banks/connect", request);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async confirmOtp(request: ConfirmOtpRequest): Promise<ConfirmOtpResult> {
    const response = await apiClient.post<
      StandardResponse<ConfirmOtpResult> | ErrorResponse
    >("/api/v1/admin/tpaygate/banks/confirm-otp", request);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },

  async disconnectBank(request: DisconnectBankRequest): Promise<void> {
    const response = await apiClient.post<MessageResponse | ErrorResponse>(
      "/api/v1/admin/tpaygate/banks/disconnect",
      request,
    );
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
  },

  /**
   * Sends a simulated payment callback through the real settlement. A 200 means the simulation
   * ran, not that the bill settled — read `outcome`.
   */
  async simulateWebhook(
    request: SimulateWebhookRequest,
  ): Promise<WebhookSimulationResult> {
    const response = await apiClient.post<
      StandardResponse<WebhookSimulationResult> | ErrorResponse
    >("/api/v1/admin/tpaygate/webhook-simulations", request);
    if (isErrorResponse(response.data))
      throw new Error(response.data.error.message);
    return response.data.data;
  },
};
